'use client';

import { useState } from 'react';
// Deep import rather than the barrel — see header-analyzer-tool.tsx.
import { checkEmailContent, type ContentCheckResult } from '@aegis/shared/src/utils/email-content-checker';
import { AlertCircle, Info, Lock, XCircle } from 'lucide-react';

/** Scores a draft entirely in the browser — the body never reaches our servers. */
export function SpamCheckerTool() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<ContentCheckResult | null>(null);

  function check(e: React.FormEvent) {
    e.preventDefault();
    setResult(checkEmailContent({ subject, body, html: /<[a-z][\s\S]*>/i.test(body) }));
  }

  const scoreColor =
    !result ? ''
    : result.score >= 80 ? 'text-emerald-600 dark:text-emerald-400'
    : result.score >= 55 ? 'text-amber-600 dark:text-amber-400'
    : 'text-red-600 dark:text-red-400';

  const ICONS = { critical: XCircle, warning: AlertCircle, info: Info };

  return (
    <div>
      <p className="mb-4 flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-2 text-xs font-medium text-primary">
        <Lock className="h-3.5 w-3.5" />
        Scored in your browser. Your draft is never sent to us.
      </p>

      <form onSubmit={check} className="space-y-4">
        <div>
          <label htmlFor="subject" className="text-sm font-medium">Subject line</label>
          <input
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <div>
          <label htmlFor="body" className="text-sm font-medium">Message body</label>
          <textarea
            id="body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="mt-2 w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:border-primary"
          />
        </div>
        <button
          type="submit"
          disabled={!body.trim() && !subject.trim()}
          className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          Check
        </button>
      </form>

      {result && (
        <div className="mt-8">
          <div className="flex items-baseline gap-2">
            <span className={`text-4xl font-bold ${scoreColor}`}>{result.score}</span>
            <span className="text-sm text-muted-foreground">/ 100</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {result.stats.word_count} words · {result.stats.link_count} link(s) ·{' '}
            {result.stats.spam_phrase_count} flagged phrase(s)
          </p>

          {result.issues.length === 0 ? (
            <p className="mt-6 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
              Nothing flagged. Content is only part of deliverability — check your domain records too.
            </p>
          ) : (
            <ul className="mt-6 space-y-3">
              {result.issues.map((issue) => {
                const Icon = ICONS[issue.severity];
                return (
                  <li key={issue.code} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                    <Icon
                      className={`mt-0.5 h-4 w-4 shrink-0 ${
                        issue.severity === 'critical'
                          ? 'text-red-600 dark:text-red-400'
                          : issue.severity === 'warning'
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-blue-600 dark:text-blue-400'
                      }`}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <p className="text-sm">{issue.message}</p>
                      {issue.evidence && (
                        <p className="mt-1 break-words font-mono text-xs text-muted-foreground">{issue.evidence}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
